/* eslint-disable react-hooks/exhaustive-deps */
import './styles.css'

import axios from 'axios'
import toast from 'react-hot-toast'
import packageJson from 'package.json'
import Icon from 'src/components/atoms/icon'
// import HelperBox from 'src/components/molecules/helper-box'
import MenuSkeleton from 'src/components/atoms/menu-skeleton'
import LogoVertical from 'src/assets/image/logo-vertical.svg?react'
import LogoHorizontal from 'src/assets/image/logo-horizontal.svg?react'
import MenuCollapseButton from 'src/components/atoms/menu-collapse-button'

import { useState } from 'react'
import { primaryMenus } from './data'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMenuQuery } from 'src/queries/use-menu'
import { useMenuStore, useUserStore } from 'src/stores'
import { infocenterGetTokenUrl } from 'src/utils/axios'
import { Flex, Layout, Menu, MenuProps, Typography } from 'antd'
import { usePermissionsStore } from 'src/stores/permissionsStore'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  formatMenuItems,
  getSelectedMenuKeys,
  removeIsSelectedFromMenuItems,
} from 'src/utils/function'

const INFOCENTRE_REDIRECT_URL =
  'https://infocentre.pli-petronas.com/account/redirect-token'

const HANGFIRE_REDIRECT_URL = import.meta.env.VITE_HANGFIRE_REDIRECT_URI

interface LevelKeysProps {
  key?: string
  children?: LevelKeysProps[]
}

function Sidebar() {
  const { user } = useUserStore()
  const { i18n } = useTranslation()

  const navigate = useNavigate()
  const location = useLocation()

  const { setPermissions } = usePermissionsStore()

  const [stateOpenKeys, setStateOpenKeys] = useState<string[]>([])
  const [collapsed, items, setItems] = useMenuStore((state) => [
    state.collapsed,
    state.items,
    state.setItems,
  ])

  const pathname = location.pathname

  const { data: menuItems, isLoading } = useMenuQuery()

  const handleRedirect = async () => {
    try {
      const params = {
        user: import.meta.env.VITE_INFOCENTRE_USER,
        password: import.meta.env.VITE_INFOCENTRE_PASSWORD,
      }
      const token = await axios
        .post(infocenterGetTokenUrl, {}, { params })
        .then((res) => res.data)

      if (token) {
        window.open(
          `${INFOCENTRE_REDIRECT_URL}?token=${token}&emailUser=${user?.email}`,
          '_blank'
        )
      }
    } catch (err) {
      toast.error(i18n.t('error.unspecific'))
    }
  }

  const handleClick: MenuProps['onClick'] = (e) => {
    if (e.key === 'infocentre') {
      handleRedirect()
      return null
    }

    if (e.key === 'it-import-import_hangfire') {
      window.open(HANGFIRE_REDIRECT_URL, '_blank')
    }

    navigate(e.key)
  }

  useEffect(() => {
    if (menuItems?.Menus) {
      setItems(menuItems?.Menus)
    }

    if (menuItems?.Permissions) {
      setPermissions(menuItems?.Permissions)
    }
  }, [menuItems, setItems])

  const formattedItems = useMemo(() => formatMenuItems(items, i18n.t), [items])

  const mappedItems = useMemo(
    () => [
      {
        key: '/dashboard',
        label: i18n.t('menu.dashboard'),
        icon: <Icon name="LayoutDashboard" size={16} strokeWidth={2} />,
        isSelected: (path: string) => path.includes('/dashboard'),
      },
      ...formattedItems,
      {
        key: 'infocentre',
        label: i18n.t('menu.goToInfocentre'),
        icon: <Icon name="ExternalLink" size={16} strokeWidth={2} />,
        isSelected: () => false,
      },
    ],
    [formattedItems, i18n.t]
  )

  const selectedOrOpenKeys = useMemo(
    () => getSelectedMenuKeys(mappedItems, pathname),
    [mappedItems, pathname]
  )

  useEffect(() => {
    if (
      selectedOrOpenKeys.length === 1 &&
      selectedOrOpenKeys[0] === '/dashboard'
    ) {
      setStateOpenKeys([])
    }
  }, [selectedOrOpenKeys])

  const getLevelKeys = (levelKeys: LevelKeysProps[]) => {
    const key: Record<string, number> = {}
    const func = (levelKeyItems: LevelKeysProps[], level = 1) => {
      levelKeyItems.forEach((item) => {
        if (item.key) {
          key[item.key] = level
        }
        if (item.children) {
          func(item.children, level + 1)
        }
      })
    }
    func(levelKeys)
    return key
  }

  const levelKeys = getLevelKeys(items as LevelKeysProps[])

  const onOpenChange: MenuProps['onOpenChange'] = (openKeys) => {
    const currentOpenKey = openKeys.find(
      (key) => stateOpenKeys.indexOf(key) === -1
    )
    if (currentOpenKey !== undefined) {
      const repeatIndex = openKeys
        .filter((key) => key !== currentOpenKey)
        .findIndex((key) => levelKeys[key] === levelKeys[currentOpenKey])

      setStateOpenKeys(openKeys.filter((_, index) => index !== repeatIndex))
    } else {
      setStateOpenKeys(openKeys)
    }
  }

  useEffect(() => {
    const activeMenu = primaryMenus.find((menu) => {
      return menu.paths.includes(pathname)
    })

    if (activeMenu && !collapsed) {
      setStateOpenKeys([activeMenu.name])
    }
  }, [collapsed])

  return (
    <Layout.Sider
      width={200}
      collapsedWidth={50}
      data-testid="sidebar"
      collapsed={collapsed}
      className="animate-duration-0 z-[2001] !bg-bg-base"
    >
      <Flex
        align="center"
        justify="space-between"
        className={`h-16 w-full px-4 ${collapsed ? 'pl-2' : 'pl-4'}`}
      >
        <Link to="/dashboard" style={{ width: '100%' }}>
          {collapsed ? (
            <LogoVertical data-testid="logo-header" className="w-8" />
          ) : (
            <LogoHorizontal data-testid="logo-header" />
          )}
        </Link>
      </Flex>

      <Flex vertical justify="space-between" className="h-[90%]">
        <MenuCollapseButton />

        {formattedItems.length === 0 && isLoading ? (
          <MenuSkeleton />
        ) : (
          <Menu
            mode="inline"
            items={removeIsSelectedFromMenuItems(mappedItems)}
            onClick={handleClick}
            openKeys={stateOpenKeys}
            data-testid="sidebar-menu"
            onOpenChange={onOpenChange}
            defaultOpenKeys={['reporting']}
            selectedKeys={selectedOrOpenKeys}
            defaultSelectedKeys={selectedOrOpenKeys}
            className="hover-scrollbar scrollbar-small custom-menu sidebar-menu border-inline-end-none"
          />
        )}

        {/* <HelperBox /> */}

        <Typography.Text className="text-center text-xs text-gray-400">
          v{packageJson.version}
          {import.meta.env.VITE_MODE !== 'production' &&
            ` - ${import.meta.env.VITE_MODE}`}
        </Typography.Text>
      </Flex>
    </Layout.Sider>
  )
}

export default Sidebar
